/**
 * Этап 5 / KS-02 — typed client + Query hooks for the contacts directory.
 *
 * Endpoints (packages/api/src/routes/clients.ts, project-contacts.ts):
 *   GET    /directory/clients                 → clients[] + unassigned_contacts[]
 *   GET    /directory/clients/duplicates      → suspected duplicate groups
 *   GET    /directory/clients/:id             → client + contacts[] + projects[]
 *   GET    /directory/clients/:id/contacts    → the picker's list
 *   POST   /directory/clients                 → create
 *   PATCH  /directory/clients/:id             → edit
 *   DELETE /directory/clients/:id             → 409 while referenced
 *   POST   /directory/clients/:id/merge       → preview (confirm:false) / merge
 *   … the same five for /directory/contacts
 *   GET|PUT /projects/:id/contacts            → the project's addressee set
 *
 * ── Why `/directory` and not `/clients`.
 * `GET /clients` is the AUTOCOMPLETE CACHE (`useClientsCache`, a bare `{id,name}[]`)
 * that the client picker already uses. The directory is a different, richer resource,
 * so it has its own namespace rather than overloading a live path.
 *
 * Every response is parsed against its `@eer/shared` schema at this boundary: a route
 * that drifts fails here, loudly, instead of rendering half a screen.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  clientContactsResponseSchema,
  clientDetailResponseSchema,
  clientMutationResponseSchema,
  clientsListResponseSchema,
  contactDetailResponseSchema,
  contactMutationResponseSchema,
  duplicatesResponseSchema,
  mergeResponseSchema,
  projectContactsResponseSchema,
  type ClientDTO,
  type ClientCreateRequest,
  type ClientPatchRequest,
  type ClientDetailResponse,
  type ClientsListResponse,
  type ContactDTO,
  type ContactCreateRequest,
  type ContactDetailResponse,
  type ContactPatchRequest,
  type DuplicatesResponse,
  type MergePreviewDTO,
  type ProjectContactsResponse,
  type SetProjectContactsRequest,
} from '@eer/shared';
import { apiClient, unwrap } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

// ---------------------------------------------------------------------------
// fetchers
// ---------------------------------------------------------------------------

export async function fetchDirectory(): Promise<ClientsListResponse> {
  return clientsListResponseSchema.parse(
    unwrap(await apiClient.get<unknown>('/directory/clients')),
  );
}

export async function fetchClientDetail(clientId: number): Promise<ClientDetailResponse> {
  return clientDetailResponseSchema.parse(
    unwrap(await apiClient.get<unknown>(`/directory/clients/${clientId}`)),
  );
}

/** The picker's data source: every contact of one client, unpaged. */
export async function fetchClientContacts(clientId: number): Promise<readonly ContactDTO[]> {
  const body = clientContactsResponseSchema.parse(
    unwrap(await apiClient.get<unknown>(`/directory/clients/${clientId}/contacts`)),
  );
  return body.contacts;
}

export async function fetchContactDetail(contactId: number): Promise<ContactDetailResponse> {
  return contactDetailResponseSchema.parse(
    unwrap(await apiClient.get<unknown>(`/directory/contacts/${contactId}`)),
  );
}

export async function fetchDuplicates(): Promise<DuplicatesResponse> {
  return duplicatesResponseSchema.parse(
    unwrap(await apiClient.get<unknown>('/directory/clients/duplicates')),
  );
}

export async function fetchProjectContacts(projectId: number): Promise<ProjectContactsResponse> {
  return projectContactsResponseSchema.parse(
    unwrap(await apiClient.get<unknown>(`/projects/${projectId}/contacts`)),
  );
}

// ---------------------------------------------------------------------------
// query keys
// ---------------------------------------------------------------------------

/**
 * Directory keys live under their OWN domain prefix, deliberately NOT under
 * `qk.clients` — that one belongs to the autocomplete cache, and sharing it would
 * make one mutation invalidate the other resource's cache for no reason.
 */
export const directoryKeys = {
  all: ['directory'] as const,
  list: () => ['directory', 'list'] as const,
  duplicates: () => ['directory', 'duplicates'] as const,
  client: (id: number) => ['directory', 'client', id] as const,
  clientContacts: (id: number) => ['directory', 'client', id, 'contacts'] as const,
  contact: (id: number) => ['directory', 'contact', id] as const,
  projectContacts: (projectId: number) => ['directory', 'project', projectId] as const,
} as const;

// ---------------------------------------------------------------------------
// read hooks
// ---------------------------------------------------------------------------

export function useDirectory(enabled = true): UseQueryResult<ClientsListResponse> {
  return useQuery({ queryKey: directoryKeys.list(), queryFn: fetchDirectory, enabled });
}

export function useClientDetail(clientId: number | null): UseQueryResult<ClientDetailResponse> {
  return useQuery({
    queryKey: directoryKeys.client(clientId ?? 0),
    queryFn: () => fetchClientDetail(clientId as number),
    enabled: clientId !== null,
  });
}

export function useClientContacts(clientId: number | null): UseQueryResult<readonly ContactDTO[]> {
  return useQuery({
    queryKey: directoryKeys.clientContacts(clientId ?? 0),
    queryFn: () => fetchClientContacts(clientId as number),
    enabled: clientId !== null,
  });
}

export function useContactDetail(contactId: number | null): UseQueryResult<ContactDetailResponse> {
  return useQuery({
    queryKey: directoryKeys.contact(contactId ?? 0),
    queryFn: () => fetchContactDetail(contactId as number),
    enabled: contactId !== null,
  });
}

export function useDuplicates(enabled = true): UseQueryResult<DuplicatesResponse> {
  return useQuery({
    queryKey: directoryKeys.duplicates(),
    queryFn: fetchDuplicates,
    enabled,
  });
}

export function useProjectContacts(
  projectId: number | null,
): UseQueryResult<ProjectContactsResponse> {
  return useQuery({
    queryKey: directoryKeys.projectContacts(projectId ?? 0),
    queryFn: () => fetchProjectContacts(projectId as number),
    enabled: projectId !== null,
  });
}

// ---------------------------------------------------------------------------
// mutations
// ---------------------------------------------------------------------------

/**
 * Invalidate everything the directory shows.
 *
 * Coarse ON PURPOSE. Every write here can change a count, a duplicate group and a
 * client's contact list at once (creating a contact changes `contacts_count`, may
 * form a new duplicate group, and appears in the picker). Working out the precise
 * key set per mutation would be a second model of the data to keep in sync; the
 * directory is a handful of small lists, so the whole prefix goes.
 */
function useInvalidateDirectory(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: directoryKeys.all });
    // The autocomplete cache renders client NAMES, so a client rename/merge must
    // reach it too — a different domain, hence a second explicit invalidation.
    await qc.invalidateQueries({ queryKey: qk.clients.all });
  };
}

export function useCreateClient(): UseMutationResult<ClientDTO, Error, ClientCreateRequest> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async (input) => {
      const body = clientMutationResponseSchema.parse(
        unwrap(await apiClient.post<unknown>('/directory/clients', input)),
      );
      return body.client;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateClient(): UseMutationResult<
  ClientDTO,
  Error,
  { id: number; patch: ClientPatchRequest }
> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const body = clientMutationResponseSchema.parse(
        unwrap(await apiClient.patch<unknown>(`/directory/clients/${id}`, patch)),
      );
      return body.client;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteClient(): UseMutationResult<void, Error, number> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async (id) => {
      unwrap(await apiClient.delete<unknown>(`/directory/clients/${id}`));
    },
    onSuccess: invalidate,
  });
}

export function useCreateContact(): UseMutationResult<ContactDTO, Error, ContactCreateRequest> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async (input) => {
      const body = contactMutationResponseSchema.parse(
        unwrap(await apiClient.post<unknown>('/directory/contacts', input)),
      );
      return body.contact;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateContact(): UseMutationResult<
  ContactDTO,
  Error,
  { id: number; patch: ContactPatchRequest }
> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const body = contactMutationResponseSchema.parse(
        unwrap(await apiClient.patch<unknown>(`/directory/contacts/${id}`, patch)),
      );
      return body.contact;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteContact(): UseMutationResult<void, Error, number> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async (id) => {
      unwrap(await apiClient.delete<unknown>(`/directory/contacts/${id}`));
    },
    onSuccess: invalidate,
  });
}

export interface MergeVars {
  readonly kind: 'clients' | 'contacts';
  readonly targetId: number;
  readonly sourceIds: readonly number[];
  /** False (the default) asks "what would this do" and writes nothing. */
  readonly confirm?: boolean;
}

export interface MergeOutcome {
  readonly merged: boolean;
  readonly preview: MergePreviewDTO;
}

/**
 * Preview or perform a merge.
 *
 * One hook for both calls because they are the same request with one flag flipped,
 * and splitting them would let the confirm call be sent with different arguments
 * than the preview the user actually approved.
 *
 * The cache is only invalidated when something was WRITTEN — a preview must not
 * refetch the lists the user is looking at.
 */
export function useMerge(): UseMutationResult<MergeOutcome, Error, MergeVars> {
  const invalidate = useInvalidateDirectory();
  return useMutation({
    mutationFn: async ({ kind, targetId, sourceIds, confirm = false }) => {
      const body = mergeResponseSchema.parse(
        unwrap(
          await apiClient.post<unknown>(`/directory/${kind}/${targetId}/merge`, {
            source_ids: [...sourceIds],
            confirm,
          }),
        ),
      );
      return { merged: body.merged, preview: body.preview };
    },
    onSuccess: async (outcome) => {
      if (outcome.merged) await invalidate();
    },
  });
}

/**
 * Replace a project's addressee set.
 *
 * Also invalidates the PROJECT detail: `contact_name` / `contact_email` on the card
 * are rendered from the primary link, so leaving the project cache alone would show
 * the previous contact until the next reload — the "saved but nothing changed"
 * report this epic exists to prevent.
 */
export function useSetProjectContacts(
  projectId: number,
): UseMutationResult<ProjectContactsResponse, Error, SetProjectContactsRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      projectContactsResponseSchema.parse(
        unwrap(await apiClient.put<unknown>(`/projects/${projectId}/contacts`, input)),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: directoryKeys.projectContacts(projectId) });
      await qc.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
      await qc.invalidateQueries({ queryKey: directoryKeys.list() });
    },
  });
}
